import {
    useCall,
    useCallClient,
    useChatClient,
    usePropsFor,
    VideoGallery,
} from '@azure/communication-react';
import { IStyle, mergeStyles, Stack } from '@fluentui/react';
import React, { useCallback, useMemo, useState } from 'react';
import { useAppContext } from '../app-state/AppContext';
import {
    ActiveElementAndVideoGalleryLayout,
    CallWithSidepaneLayout,
    findParticipantInCallById,
    isAskTeacherThreadTopic,
    LocalPreviewTile,
    MutedNotification,
    mutedNotificationSelector,
    notificationsContainerStyles,
    pcpTokens,
    selectActiveElement,
    selectIsMobile,
    sendIsAllowedToSpeak,
    SidePane,
    SIDEPANE_WIDTH,
    transformTopicForModerator,
    useActivatedChatThreadTopicName,
    useCallAdapter,
    useDataChannel,
    usePcpLiveLessonContext,
} from 'live-lesson/shared/feature-call-ui-react';
import { Features } from '@azure/communication-calling';
import { ModeratorVideoGallery } from '../components/gallery/ModeratorVideoGallery';
import { toPcpGalleryPagerProps, useParticipantsPagination } from '../../hooks/useParticipantsPagination';
import { ActiveElementAndLocalPreviewTile } from '../components/gallery/ActiveElementAndLocalPreviewTile';
import { PcpControlBar } from '../components/PcpControlBar';
import { useRemoveParticipantFromCall } from '../../hooks/removeParticipantFromCall';
import { CallParticipantListSidePaneItem } from '../components/sidePaneItems/CallParticipantListSidePaneItem';
import { ChatThreadSwitcherSidePaneItem } from '../components/sidePaneItems/ChatThreadSwitcherListSidePaneItem';
import { CreateMenuItemsCallbacks } from '../components/util/createMenuItems';
import { PcpGalleryPager } from '../components/PcpGalleryPager';

const callWithSidepaneLayoutStyles = {
    root: { overflowY: 'hidden' }, //makes 'height' match the parent element's 'display:flex'
    main: {
        paddingLeft: '0.5rem',
        paddingRight: '0.5rem',
    },
};

const pageStyle = {
    background: pcpTokens.pcpColorNeutralBackground6Dark,
    height: '100%',
} as IStyle;


function CallPage(): JSX.Element {

    const mutedNotificationProps = usePcpLiveLessonContext(mutedNotificationSelector);
    const [callEnded, setCallEnded] = useState(false);
    const {
        isSidePaneOpen,
        sidePaneContent,
        addSidePaneContent,
        toggleSidePaneContent,
        removeSidePaneContent,
    } = useAppContext();

    const isMobile = usePcpLiveLessonContext(selectIsMobile);
    const activeElement = usePcpLiveLessonContext(selectActiveElement);

    const isChatOpen = useMemo(() => sidePaneContent.includes('chat'), [sidePaneContent]);
    const isParticipantsOpen = useMemo(() => sidePaneContent.includes('participants'), [sidePaneContent]);
    const closeSidePaneContent = (contentName: 'chat'|'participants') => removeSidePaneContent(contentName);
    const closeParticipantsPane = useCallback(() => closeSidePaneContent('participants'), [closeSidePaneContent]);
    const closeChatPane = useCallback(() => closeSidePaneContent('chat'), [closeSidePaneContent]);

    const [isShowingActiveElement, setIsShowingActiveElement] = useState(true);
    const toggleActiveElement = useCallback(() => {
        setIsShowingActiveElement(!isShowingActiveElement);
    }, [isShowingActiveElement]);


    if (callEnded) {
        return <CallEnded />;
    }

    const call = useCall();
    const callAdapter = useCallAdapter();
    const callClient = useCallClient();
    const activateChat = usePcpLiveLessonContext(s => s.setActiveThreadId);
    const chatClient = useChatClient();

    // TODO: check hangUp on window close
    // useEffect(() => {
    //     const disposeCall = () => call?.hangUp();
    //     window.addEventListener('beforeunload', disposeCall);
    //     return () => window.removeEventListener('beforeunload', disposeCall);
    // }, [call]);

    const muteParticipant = useCallback(async (participantId: string) => {
        findParticipantInCallById(call, participantId)?.mute();
    }, [call]);

    const role = callAdapter.getState().call?.role;
    const canUnmute = role !== "Consumer";

    const lowerHandAndUnmute = useCallback(async (participantId: string) => {
        // const identifier: CommunicationUserIdentifier = fromFlatCommunicationIdentifier(participantId);
        call?.feature(Features.RaiseHand).lowerHands([{ communicationUserId: participantId }]);
        allowToSpeak(participantId);
    }, [call]);

    const lowerHandWithoutUnmute = useCallback(async (participantId: string) => {
        call?.feature(Features.RaiseHand).lowerHands([{ communicationUserId: participantId }]);
    }, [call]);

    const allowToSpeak = useCallback((participantId: string) => {
        sendIsAllowedToSpeak(call, { communicationUserId: participantId });
    }, [call]);

    const { removeParticipantWithConfirmCallback, confirmRemoveParticipantDialog } = useRemoveParticipantFromCall();

    // const removeParticipantWithConfirm = useCallback(() => {
    //
    // });

    const onSendDirectMessage = useCallback(async (participantId: string) => {
        const thread = Object.values(chatClient.getState().threads)
            // .find(async thread => {
            .find(thread => {
                if (!isAskTeacherThreadTopic(thread.properties?.topic)) {
                    return false;
                }

                const participantIds = Object.keys(thread.participants);
                return participantIds.includes(participantId);
            });

        if (thread) {
            addSidePaneContent('chat');
            activateChat(thread.threadId);
        }
    }, [chatClient]);

    // eslint-disable-next-line no-console -- React component, no DI available
    const onDataChannelMessageReceived = (message: any) => console.log("onDataChannelMessageReceived", message);
    const { sendCustomEvent } = useDataChannel(call, onDataChannelMessageReceived);
    // const sendEvent = () => {
    //     sendCustomEvent('customEventName', { key: 'value' });
    // };


    const videoGalleryProps = usePropsFor(VideoGallery);
    // calculated by the ACS framework: optimal number of video streams to show based on factors like screen size, network connection and device type
    const optimalVideoCount = useCall()?.feature(Features.OptimalVideoCount).optimalVideoCount;
    // maximum number of remote video streams to show
    const maxRemoteVideoStreams = 6;
    const participantsPerPage = 15;
    const moderatorGalleryProps = useParticipantsPagination(participantsPerPage, videoGalleryProps.remoteParticipants);
    const galleryPagerProps = toPcpGalleryPagerProps(moderatorGalleryProps);
    const numTotalParticipants = videoGalleryProps.remoteParticipants.length;
    const localParticipant = videoGalleryProps.localParticipant;

    // renders the moderator's local video stream
    const localPreviewTile = useMemo(() => <LocalPreviewTile localParticipant={localParticipant} />, [localParticipant]);

    // moderator local video shown in the gallery
    // => only shown if the active element is not shown
    const galleryLocalPreviewTile = useMemo(
        () => isShowingActiveElement ? undefined : localPreviewTile,
        [isShowingActiveElement, localPreviewTile]
    );


    // moderator local video shown next to the active element
    // => when the active element is shown, show the local video next to the active element and hide it from the gallery
    const activeElementLocalPreviewTile = useMemo(
        () => isShowingActiveElement ? localPreviewTile : <></>,
        [isShowingActiveElement, localPreviewTile]
    );

    const activatedChatTopic = useActivatedChatThreadTopicName(transformTopicForModerator);

    const menuItemCallbacks: CreateMenuItemsCallbacks = useMemo(() => ({
        muteParticipant,
        lowerHandAndUnmute,
        lowerHandWithoutUnmute,
        removeParticipant: removeParticipantWithConfirmCallback,
        onSendDirectMessage,
    }), [muteParticipant, lowerHandAndUnmute, lowerHandWithoutUnmute, removeParticipantWithConfirmCallback, onSendDirectMessage]);

    const sidePaneMode = useMemo(() => isMobile ? 'overlay' : 'side', [isMobile]);

    return (
        <Stack className={mergeStyles(pageStyle)}>
            {confirmRemoveParticipantDialog}
            <CallWithSidepaneLayout
                isMobile={isMobile}
                sidepaneMode={sidePaneMode}

                mainContent={
                    <>
                        <Stack.Item styles={notificationsContainerStyles}>
                            {canUnmute && (
                                // {canUnmute && !!props.mutedNotificationProps && (
                                <MutedNotification speakingWhileMuted={mutedNotificationProps.speakingWhileMuted} />
                            )}
                        </Stack.Item>

                        <ActiveElementAndVideoGalleryLayout>
                            {activeElement && isShowingActiveElement && (
                                <ActiveElementAndLocalPreviewTile
                                    activeElement={activeElement}
                                    localPreviewTile={activeElementLocalPreviewTile}
                                />
                            )}

                            <ModeratorVideoGallery
                                {...moderatorGalleryProps}
                                localPreviewTile={galleryLocalPreviewTile}
                                videoGalleryProps={videoGalleryProps}
                                menuItemCallbacks={menuItemCallbacks}
                            />

                        </ActiveElementAndVideoGalleryLayout>
                    </>
                }

                sidePaneContent={
                    <SidePane isOverlay={isMobile} isOpen={isSidePaneOpen} header={""} width={SIDEPANE_WIDTH}>
                        <CallParticipantListSidePaneItem
                            isOpen={isParticipantsOpen}
                            close={closeParticipantsPane}
                            isMobile={isMobile}
                            numTotalParticipants={numTotalParticipants}
                            menuItemCallbacks={menuItemCallbacks}
                        />
                        <ChatThreadSwitcherSidePaneItem
                            isOpen={isChatOpen}
                            close={closeChatPane}
                            isMobile={isMobile}
                            activatedChatTopic={activatedChatTopic}
                        />
                    </SidePane>
                }
                isSidePaneOpen={isSidePaneOpen}
                styles={callWithSidepaneLayoutStyles}
            >
                {/* TODO: add notifications */}
                {/*{props.errorBarProps !== false && (*/}
                {/*    <Stack styles={bannerNotificationStyles}>*/}
                {/*        <ErrorBar*/}
                {/*            {...props.errorBarProps}*/}
                {/*            onDismissError={props.onDismissError}*/}
                {/*            activeErrorMessages={filteredLatestErrors}*/}
                {/*        />*/}
                {/*    </Stack>*/}
                {/*)}*/}
            </CallWithSidepaneLayout>

            <Stack horizontal tokens={{childrenGap: '1.25rem'}}>
                <Stack.Item grow>&nbsp;</Stack.Item>
                <Stack.Item style={{marginLeft: 0}}>
                    <PcpControlBar
                        isChatOpen={isChatOpen}
                        isParticipantsOpen={isParticipantsOpen}
                        toggleActiveElement={toggleActiveElement}
                        isShowingActiveElement={isShowingActiveElement}
                        setCallEnded={setCallEnded}
                    ></PcpControlBar>
                </Stack.Item>
                <Stack.Item grow
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    {/* Paginate remote video tiles */}
                    {galleryPagerProps.hasMultiplePages &&
                        <PcpGalleryPager {...galleryPagerProps} />
                    }
                </Stack.Item>
            </Stack>

        </Stack>
    );
}

function CallEnded(): JSX.Element {
    return <h1>You ended the call.</h1>;
}

export default CallPage;
